/**
 * US → UK English spelling replacements for content displayed
 * in en-GB, en-IE, and en-AU locales.
 *
 * This runtime swap approach works well while overrides are limited to
 * currency symbols and a handful of spelling changes. If locale-specific
 * copy grows beyond this (custom CTAs, regulatory text, different social
 * proof), consider generating full content files per locale instead —
 * similar to how DE/ES/NL already have dedicated section data.
 *
 * Only includes words that actually appear in shared section content.
 * Add new entries as needed when new US spellings are found.
 */

/** US word → UK word (both lowercase). Case is preserved at replacement time. */
export const WORD_PAIRS: [string, string][] = [
  ["analyze", "analyse"],
  ["analyzed", "analysed"],
  ["analyzes", "analyses"],
  ["analyzing", "analysing"],
  ["optimize", "optimise"],
  ["optimized", "optimised"],
  ["optimizes", "optimises"],
  ["optimizing", "optimising"],
  ["customize", "customise"],
  ["customized", "customised"],
  ["customizes", "customises"],
  ["customizing", "customising"],
  ["customization", "customisation"],
  ["visualize", "visualise"],
  ["visualized", "visualised"],
  ["visualizes", "visualises"],
  ["visualizing", "visualising"],
  ["visualization", "visualisation"],
  ["visualizations", "visualisations"],
  ["recognize", "recognise"],
  ["recognized", "recognised"],
  ["recognizes", "recognises"],
  ["recognizing", "recognising"],
  ["maximize", "maximise"],
  ["maximized", "maximised"],
  ["maximizes", "maximises"],
  ["maximizing", "maximising"],
  ["minimize", "minimise"],
  ["minimized", "minimised"],
  ["minimizes", "minimises"],
  ["minimizing", "minimising"],
  ["utilize", "utilise"],
  ["utilized", "utilised"],
  ["utilizes", "utilises"],
  ["utilizing", "utilising"],
  ["prioritize", "prioritise"],
  ["prioritized", "prioritised"],
  ["prioritizes", "prioritises"],
  ["prioritizing", "prioritising"],
  ["specialize", "specialise"],
  ["specialized", "specialised"],
  ["specializes", "specialises"],
  ["specializing", "specialising"],
  ["finalize", "finalise"],
  ["finalized", "finalised"],
  ["finalizes", "finalises"],
  ["finalizing", "finalising"],
  ["monetize", "monetise"],
  ["monetized", "monetised"],
  ["monetizes", "monetises"],
  ["monetizing", "monetising"],
  ["personalize", "personalise"],
  ["personalized", "personalised"],
  ["personalizes", "personalises"],
  ["personalizing", "personalising"],
  ["personalization", "personalisation"],
  ["organize", "organise"],
  ["organized", "organised"],
  ["organizes", "organises"],
  ["organizing", "organising"],
  ["organization", "organisation"],
  ["organizations", "organisations"],
  ["capitalize", "capitalise"],
  ["capitalized", "capitalised"],
  ["capitalizes", "capitalises"],
  ["capitalizing", "capitalising"],
  ["standardize", "standardise"],
  ["standardized", "standardised"],
  ["standardizes", "standardises"],
  ["standardizing", "standardising"],
  ["standardization", "standardisation"],
  ["emphasize", "emphasise"],
  ["emphasized", "emphasised"],
  ["emphasizes", "emphasises"],
  ["emphasizing", "emphasising"],
  ["categorize", "categorise"],
  ["categorized", "categorised"],
  ["categorizes", "categorises"],
  ["categorizing", "categorising"],
  ["summarize", "summarise"],
  ["summarized", "summarised"],
  ["summarizes", "summarises"],
  ["summarizing", "summarising"],
  ["authorize", "authorise"],
  ["authorized", "authorised"],
  ["authorizes", "authorises"],
  ["authorizing", "authorising"],
  ["authorization", "authorisation"],
  ["color", "colour"],
  ["colors", "colours"],
  ["colored", "coloured"],
  ["coloring", "colouring"],
  ["behavior", "behaviour"],
  ["behaviors", "behaviours"],
  ["favor", "favour"],
  ["favors", "favours"],
  ["favored", "favoured"],
  ["favoring", "favouring"],
  ["favorite", "favourite"],
  ["favorites", "favourites"],
  ["honor", "honour"],
  ["honors", "honours"],
  ["honored", "honoured"],
  ["honoring", "honouring"],
  ["neighbor", "neighbour"],
  ["neighbors", "neighbours"],
  ["labor", "labour"],
  ["labors", "labours"],
  ["theater", "theatre"],
  ["theaters", "theatres"],
  ["center", "centre"],
  ["centers", "centres"],
  ["centered", "centred"],
  ["centering", "centring"],
  ["installment", "instalment"],
  ["installments", "instalments"],
  ["fulfill", "fulfil"],
  ["fulfills", "fulfils"],
  ["fulfillment", "fulfilment"],
  ["fulfillments", "fulfilments"],
  ["defense", "defence"],
  ["defenses", "defences"],
  ["offense", "offence"],
  ["offenses", "offences"],
  ["license", "licence"],
  ["licenses", "licences"],
];

/** Preserve the casing of the original match when applying the replacement */
function casePreservingReplace(match: string, replacement: string): string {
  if (match === match.toUpperCase()) return replacement.toUpperCase();
  if (match[0] === match[0]?.toUpperCase()) {
    return replacement[0]!.toUpperCase() + replacement.slice(1);
  }
  return replacement;
}

/** Build regex + replacer pairs from word pairs */
const US_TO_UK = WORD_PAIRS.map(
  ([us, uk]) =>
    [
      new RegExp(`\\b${us}\\b`, "gi"),
      (match: string) => casePreservingReplace(match, uk),
    ] as const
);

/** Locales that use UK English spelling */
const UK_SPELLING_LOCALES = new Set(["en-GB", "en-IE", "en-AU"]);

/** Check if a locale uses UK English spelling */
export function usesUkSpelling(locale: string): boolean {
  return UK_SPELLING_LOCALES.has(locale);
}

/** Apply US → UK spelling replacements to a string, preserving case */
export function toUkSpelling(text: string): string {
  let result = text;
  for (const [pattern, replacer] of US_TO_UK) {
    result = result.replace(pattern, replacer);
  }
  return result;
}

/**
 * Recursively apply a string transform to all string values in an object or array.
 * Preserves non-string values (numbers, booleans, null, undefined).
 */
export function mapStringValues<T>(
  value: T,
  transform: (str: string) => string
): T {
  if (typeof value === "string") {
    return transform(value) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) =>
      mapStringValues(item, transform)
    ) as unknown as T;
  }
  if (value !== null && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      result[k] = mapStringValues(v, transform);
    }
    return result as unknown as T;
  }
  return value;
}

/**
 * Apply UK spelling transformation to an entire messages/content data object or value.
 */
export function applyUkSpellingToData<T>(data: T): T {
  return mapStringValues(data, toUkSpelling);
}
